import React, { useState, useEffect, useMemo } from 'react'
import {
  SmartList,
  Icons,
  IColumnElement,
  IHeaderItem,
  IconButton,
  PaginationPanel,
  SearchInput,
  IRowElement,
  Spinner,
  Container,
  Row,
} from '@doseme/cohesive-ui'
import { NAVY } from '@doseme/cohesive-ui'
import { faPlus } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IEntityHashMap, isLoading } from 'flux-entities'

import { TProps } from './types'
import { IInstallation } from '../../../../types/installation'
import { installationsEdit, INSTALLATIONS_CREATE_URL } from '../../../../constants/urls/frontend'
import { DISPLAY_ITEMS_PER_PAGE } from '../../../../constants/display'
import { paginate } from '../../../utils'
import { smartListData } from '../../utils'
import { IInstallationsState } from '../../../../store/installations/types'
import { alphabetical } from '../../../../utils/arrayUtils'

const cols: IHeaderItem[] = [
  {
    name: 'Code',
  },
  {
    name: 'Installation Name',
  },
  {
    name: 'Hospital Count',
  },
  {
    name: 'Actions',
  }
]

const textContent = (installation: IInstallation): string[] => [
  installation.code,
  installation.description,
]

const getSearchableContent = (
  installationIds: Array<number | string>,
  allInstallations: IEntityHashMap<IInstallation>
) => installationIds.reduce<Record<number, string[]>>(
  (acc, x) => {
    return {
      ...acc,
      [x]: textContent(allInstallations[x])
    }
  }, {}
)

const getPaginatedListWithPageCount = (
  installations: IInstallationsState,
  searchContent: Record<number, string[]>,
  searchText: string,
  columnContent: (installation: IInstallation) => IColumnElement[],
  currentPage: number,
  updateCurrentPage: (pages: number) => void,
): { totalPages: number, paginatedData: IRowElement[] } => {
  let data: IRowElement[] = []

  if (!isLoading(installations)) {
    data = smartListData({
      ids: installations.ids.sort(
        (x, y) => alphabetical(
          installations.all[x].code,
          installations.all[y].code
        )
      ),
      all: installations.all,
      searchContent,
      searchText,
      columnizeRow: columnContent
    })
  }

  const totalPages = Math.ceil(data.length / DISPLAY_ITEMS_PER_PAGE) || 1

  if (currentPage > totalPages) {
    updateCurrentPage(totalPages)
  }

  return {
    totalPages,
    paginatedData: paginate(data, { currentPage, itemsPerPage: DISPLAY_ITEMS_PER_PAGE })
  }
}

const Display: React.FC<TProps> = (props: TProps): JSX.Element | null => {
  const {
    installations,
    fetchInstallations,
    deleteInstallation,
    history
  } = props

  useEffect(() => {
    if (!installations.touched) { fetchInstallations() }
  }, [installations.touched, fetchInstallations])

  const [currentPage, updateCurrentPage] = useState(1)
  const [searchText, setSearchText] = useState('')

  const searchableContent = useMemo(() => getSearchableContent(
    installations.ids,
    installations.all,
  ), [
    installations.ids,
    installations.all,
  ])

  if (!installations.touched) {
    return null
  }

  const getDeleteButton = (installation: IInstallation): JSX.Element | null => {
    if (installation.hospitalCount === 0) {
      return (
        <Icons.DeleteIconButton
          data-testid='delete-installation'
          disabled={installation.hospitalCount > 0}
          onClick={() => confirmDelete(installation)}
        />
      )
    }
    return null
  }

  const confirmDelete = (installation: IInstallation) => {
    if (window.confirm('Are you sure you want to delete this installation? This cannot be undone.')) {
      deleteInstallation(installation)
    }
  }

  const createIcon = (
    !isLoading(installations)
      ? <IconButton
        onClick={() => history.push(INSTALLATIONS_CREATE_URL)}
        size='30px'
      >
        <FontAwesomeIcon icon={faPlus} />
      </IconButton>
      : <Spinner color={NAVY} size='lg' />
  )

  const columnContent = (installation: IInstallation): IColumnElement[] => [
    {
      name: 'code',
      element: (
        <span>
          {installation.code}
       </span>
      ),
      text: installation.code,
    },

    {
      name: 'description',
      element: (
        <span>
          {installation.description}
        </span>
      ),
      text: installation.description,
    },

    {
      name: 'hospitalCount',
      element: (
        <span>
          {installation.hospitalCount}
        </span>
      ),
      text: installation.hospitalCount?.toString(),
    },

    {
      name: 'actions',
      element: (
        <div className='d-flex p-3'>
          <Icons.EditIconButton
            data-test='edit-button'
            onClick={() => history.push(installationsEdit(installation.id))}
          />
          {getDeleteButton(installation)}
        </div>
      )
    }
  ]

  const { totalPages, paginatedData } = getPaginatedListWithPageCount(
    installations,
    searchableContent,
    searchText,
    columnContent,
    currentPage,
    updateCurrentPage
  )

  return (
    <Container>
      <div className='d-flex justify-content-between my-2'>
        <div className='d-flex align-items-center'>
          <h4 className='co-h4 pr-2' data-test='installations-list'>Installations List</h4>
          {createIcon}
        </div>
        <SearchInput
          value={searchText}
          onChange={setSearchText}
        />
      </div>
      <Row>
        <SmartList
          loading={isLoading(installations)}
          cols={cols}
          data={paginatedData}
          textIfEmpty={installations.ids.length ? 'No matching installations found.' : 'There are currently no installations.'}
        />
      </Row>
      <Row className='mt-2'>
        <PaginationPanel
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={updateCurrentPage}
        />
      </Row>
    </Container>
  )
}

export {
  Display
}
